import type { BuilderAction, BuilderNode, BuilderState } from '../schemas/builder';

export { getBuilderDataText } from './data';

export const DEFAULT_BUILDER_STATE: BuilderState = { active: false, disabled: false };

export type ServiceRequestType = 'main' | 'secondary' | 'tertiary';
export type ServiceRequestStatus = Record<ServiceRequestType, boolean>;

export const SERVICE_REQUEST_TYPES = {
  'call-waiter': 'main',
  'request-bill': 'secondary',
  'another-round': 'tertiary',
} as const;

export interface BuilderButtonState extends BuilderState {
  visible: boolean;
}

export function getBuilderButtonState(
  action: BuilderAction | undefined,
  status: ServiceRequestStatus,
  available: ServiceRequestStatus,
  pageIds: readonly string[],
): BuilderButtonState {
  if (!action) {
    return { active: false, disabled: true, visible: true };
  }

  if (action.type === 'page') {
    const visible = pageIds.includes(action.pageId);
    return { active: false, disabled: !visible, visible };
  }

  if (action.type === 'back') {
    return { active: false, disabled: false, visible: true };
  }

  const requestType = SERVICE_REQUEST_TYPES[action.request];
  const active = status[requestType];
  const anotherRequestActive = Object.entries(status).some(([type, value]) => type !== requestType && value);

  return {
    active,
    // An active request can still be cancelled if another screen changes the service state.
    disabled: !available[requestType] || (!active && anotherRequestActive),
    visible: available[requestType],
  };
}

export function resolveBuilderNode(node: BuilderNode, state: BuilderState): BuilderNode {
  const active = state.active ? node.states?.active : undefined;
  const disabled = state.disabled ? node.states?.disabled : undefined;

  return {
    ...node,
    ...active,
    ...disabled,
    style: { ...node.style, ...active?.style, ...disabled?.style },
  } as BuilderNode;
}

export function builderHasServiceRequest(node: BuilderNode): boolean {
  return (node.type === 'button' && node.action?.type === 'service-request')
    || ('children' in node && node.children.some(builderHasServiceRequest));
}
